/**
 * Fallback restaurant catalogue.
 *
 * These sixteen venues are fictional and exist only to exercise the UI. Every
 * field is here for a reason — see the `Restaurant` typedef in
 * `src/lib/types.js` for what each one drives.
 *
 * `ownership` is invented like every other field here, and deliberately mixed:
 * three of the sixteen are chains, about the share the real catalogue carries.
 * A uniform label would be worse than none, because the locally-owned filter is
 * hidden when nothing is independent and changes nothing visible when
 * everything is — either way a working filter looks broken on the one deck
 * someone sees when the API is unreachable.
 *
 * Replacing this file with a real API response is the intended next step: keep
 * the shape, drop the array. Nothing else imports the data directly except
 * `useRestaurantDeck`, so the swap is contained.
 *
 * @type {import('../lib/types.js').Restaurant[]}
 */
export const restaurants = [
  {
    id: 1,
    name: 'Hearth & Ember',
    ownership: 'independent',
    cuisine: 'New American',
    neighborhood: 'Old Market',
    price: '$$$',
    rating: 4.6,
    description:
      'Everything comes off a wood-burning hearth in this farm-to-table room — the bone marrow and the wild mushroom ravioli are the reasons to book.',
    times: [],
    waitlistOnly: true,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['romantic', 'upscale', 'business', 'intimate'],
    dietary: [],
    goodForGroups: true,
    coords: [41.8852, -87.6285],
    imageSeed: 'crave-hearth-ember',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 2,
    name: 'Marigold',
    ownership: 'independent',
    cuisine: 'Modern Mediterranean',
    neighborhood: 'Riverside',
    price: '$$$',
    rating: 4.3,
    description:
      'Lighter takes on Mediterranean classics with a long marble bar overlooking the water. The mezze board is built for sharing.',
    times: [],
    waitlistOnly: true,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['romantic', 'lively', 'upscale', 'scenic'],
    dietary: ['vegetarian-friendly'],
    goodForGroups: true,
    coords: [41.8869, -87.6221],
    imageSeed: 'crave-marigold',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 3,
    name: 'The Copper Table',
    ownership: 'independent',
    cuisine: 'Italian · Wood-Fired',
    neighborhood: 'Old Market',
    price: '$$$',
    rating: 4.4,
    description:
      'Handmade pastas and blistered pizzas from a single wood oven, backed by a wine list that leans hard into small Italian producers.',
    times: ['5:00 PM', '6:30 PM', '8:15 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['romantic', 'cozy', 'intimate', 'wine-focused'],
    dietary: ['vegetarian-friendly'],
    goodForGroups: true,
    coords: [41.8841, -87.6262],
    imageSeed: 'crave-copper-table',
    past: true,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 4,
    name: 'The Rolling Pin',
    ownership: 'chain',
    cuisine: 'Neapolitan Pizza',
    neighborhood: 'The Arcade',
    price: '$$',
    rating: 4.5,
    description:
      'Sixty-second Neapolitan pies, house-pulled mozzarella and a loud, happy room. Come early or expect to wait at the bar.',
    times: ['5:00 PM', '5:45 PM', '7:00 PM', '8:30 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['casual', 'lively', 'family-friendly', 'buzzy'],
    dietary: ['vegetarian-friendly'],
    goodForGroups: true,
    coords: [41.8808, -87.6294],
    imageSeed: 'crave-rolling-pin',
    past: true,
    upcoming: true,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 5,
    name: 'Casa Verde',
    ownership: 'independent',
    cuisine: 'Modern Mexican',
    neighborhood: 'Riverside',
    price: '$$',
    rating: 4.1,
    description:
      'An energetic patio with the largest mezcal selection in the city, signature margaritas and tacos worth the queue.',
    times: ['5:00 PM', '6:00 PM', '7:30 PM', '9:00 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['lively', 'outdoor', 'festive', 'vibrant'],
    dietary: ['vegetarian-friendly', 'gluten-free-friendly'],
    goodForGroups: true,
    coords: [41.8881, -87.6248],
    imageSeed: 'crave-casa-verde',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 6,
    name: 'Buttonwood Supper Club',
    ownership: 'independent',
    cuisine: 'American · Supper Club',
    neighborhood: 'Northgate',
    price: '$$',
    rating: 4.2,
    description:
      'Midwestern supper-club nostalgia done straight: relish trays, Friday fish fry, brandy old fashioneds and twenty-four taps.',
    times: ['5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['casual', 'lively', 'sports bar', 'family-friendly'],
    dietary: [],
    goodForGroups: true,
    coords: [41.8925, -87.6301],
    imageSeed: 'crave-buttonwood',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 7,
    name: 'Salt & Cedar',
    ownership: 'independent',
    cuisine: 'Seafood · Raw Bar',
    neighborhood: 'Warehouse District',
    price: '$$$',
    rating: 4.3,
    description:
      'A tight daily-changing raw bar, whole grilled fish and a chilled-seafood tower that arrives with an audience.',
    times: ['5:00 PM', '6:30 PM', '7:30 PM', '8:45 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['stylish', 'business', 'lively', 'upscale'],
    dietary: ['gluten-free-friendly'],
    goodForGroups: true,
    coords: [41.8794, -87.6412],
    imageSeed: 'crave-salt-cedar',
    past: true,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 8,
    name: 'Trattoria Nove',
    ownership: 'independent',
    cuisine: 'Classic Italian',
    neighborhood: 'Lakeview Commons',
    price: '$$$',
    rating: 4.4,
    description:
      'Family-run for three decades, praised for its minestrone, its Sunday gravy and a dining room that never rushes you.',
    times: ['5:30 PM', '7:00 PM', '8:30 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['quiet', 'romantic', 'intimate', 'family-owned'],
    dietary: ['vegetarian-friendly'],
    goodForGroups: false,
    coords: [41.9142, -87.6437],
    imageSeed: 'crave-trattoria-nove',
    past: true,
    upcoming: true,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 9,
    name: 'Lumen',
    ownership: 'independent',
    cuisine: 'New American · Cocktail Bar',
    neighborhood: 'The Arcade',
    price: '$$$',
    rating: 4.4,
    description:
      'Cocktail-forward small plates in a low-lit, design-obsessed room. The burrata and the crab cakes carry the menu.',
    times: ['5:30 PM', '7:00 PM', '8:30 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['upscale', 'intimate', 'romantic', 'date-night', 'trendy'],
    dietary: ['gluten-free-friendly'],
    goodForGroups: false,
    coords: [41.8817, -87.6276],
    imageSeed: 'crave-lumen',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 10,
    name: 'Little Fern',
    ownership: 'independent',
    cuisine: 'Vegetarian · Farm to Table',
    neighborhood: 'Old Market',
    price: '$$',
    rating: 4.5,
    description:
      'A vegetable-first kitchen that changes weekly with whatever the farms send. Even the sceptics leave converted.',
    times: ['5:00 PM', '6:30 PM', '8:00 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['stylish', 'quiet', 'artsy', 'all-day'],
    dietary: ['vegetarian-friendly', 'vegan-friendly', 'gluten-free-friendly'],
    goodForGroups: true,
    coords: [41.8846, -87.6249],
    imageSeed: 'crave-little-fern',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 11,
    name: 'Ash & Oak',
    ownership: 'independent',
    cuisine: 'American · Whiskey Bar',
    neighborhood: 'Warehouse District',
    price: '$$',
    rating: 4.3,
    description:
      'Comfort food with scratch-kitchen care — wood-roasted half chicken, bacon-jam burgers — beside a genuinely deep whiskey library.',
    times: ['5:00 PM', '6:00 PM', '7:30 PM', '9:00 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['cozy', 'casual', 'lively', 'whiskey-focused'],
    dietary: [],
    goodForGroups: true,
    coords: [41.8802, -87.6389],
    imageSeed: 'crave-ash-oak',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 12,
    name: 'Kismet',
    ownership: 'independent',
    cuisine: 'Modern American',
    neighborhood: 'Lakeview Commons',
    price: '$$$',
    rating: 4.6,
    description:
      'A thirty-eight seat bistro with a short seasonal menu that turns over weekly. It books out the moment slots open.',
    times: ['6:00 PM', '7:30 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['intimate', 'quiet', 'romantic', 'upscale'],
    dietary: ['vegetarian-friendly'],
    goodForGroups: false,
    coords: [41.9168, -87.6455],
    imageSeed: 'crave-kismet',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 13,
    name: 'Taqueria Del Sol',
    ownership: 'independent',
    cuisine: 'Mexican · Tacos',
    neighborhood: 'Northgate',
    price: '$',
    rating: 4.7,
    description:
      'Fresh masa pressed to order, slow-braised barbacoa and hand-blended salsas. A counter-service institution.',
    times: ['11:30 AM', '12:30 PM', '5:00 PM', '6:30 PM', '8:00 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['casual', 'family-friendly', 'lively', 'outdoor'],
    dietary: ['gluten-free-friendly', 'vegetarian-friendly'],
    goodForGroups: true,
    coords: [41.8951, -87.6338],
    imageSeed: 'crave-del-sol',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 14,
    name: 'Nori & Rye',
    ownership: 'independent',
    cuisine: 'Japanese · Sushi',
    neighborhood: 'Old Market',
    price: '$$$',
    rating: 4.5,
    description:
      'Omakase nigiri and izakaya plates with a serious Japanese whisky pour list. Minimal room, maximal detail.',
    times: ['5:30 PM', '7:00 PM', '8:30 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['intimate', 'upscale', 'quiet', 'date-night'],
    dietary: ['gluten-free-friendly'],
    goodForGroups: false,
    coords: [41.8837, -87.6271],
    imageSeed: 'crave-nori-rye',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 15,
    name: 'Highline Rooftop',
    ownership: 'chain',
    cuisine: 'American · Rooftop Bar',
    neighborhood: 'Riverside',
    price: '$$',
    rating: 4.2,
    description:
      'Twelve storeys up with a rotating tap list and shareable plates. The best sunset seat in the neighbourhood.',
    times: ['4:00 PM', '5:30 PM', '7:00 PM', '8:30 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['outdoor', 'lively', 'scenic', 'vibrant', 'all-day'],
    dietary: ['vegetarian-friendly'],
    goodForGroups: true,
    coords: [41.8875, -87.6234],
    imageSeed: 'crave-highline',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
  {
    id: 16,
    name: 'Bräu Haus',
    ownership: 'chain',
    cuisine: 'German · Beer Hall',
    neighborhood: 'The Arcade',
    price: '$$',
    rating: 4.4,
    description:
      'Forty-eight rotating taps, housemade wursts and Bavarian pretzels served down long communal tables. Loud on purpose.',
    times: ['4:00 PM', '5:00 PM', '6:30 PM', '8:00 PM'],
    waitlistOnly: false,
    memberTimes: [],
    memberExclusive: false,
    ambiance: ['lively', 'family-friendly', 'buzzy', 'casual', 'festive'],
    dietary: [],
    goodForGroups: true,
    coords: [41.8823, -87.6312],
    imageSeed: 'crave-brau-haus',
    past: false,
    upcoming: false,
    reservationUrl: null,
    instagramUrl: null,
  },
];

/**
 * Returns a copy of the catalogue in daily-seeded order.
 *
 * Used only when the live catalogue is unreachable. Same calendar day keeps
 * the same order so a reload does not jump cards; tomorrow's deck is different.
 *
 * @returns {import('../lib/types.js').Restaurant[]}
 */
export function shuffledRestaurants() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  // This catalogue is only the offline UI fallback, not a real availability
  // feed. Keep its cards visible at breakfast as well as lunch and dinner so
  // an API outage never looks like an unexplained empty Discover screen.
  return seededShuffle(restaurants, `${now.getFullYear()}-${month}-${day}`).map((restaurant) => ({
    ...restaurant,
    meals: ['breakfast', 'lunch', 'dinner'],
  }));
}

/**
 * @template T
 * @param {T[]} items
 * @param {string} seed
 * @returns {T[]}
 */
function seededShuffle(items, seed) {
  const deck = [...items];
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  let t = hash >>> 0;
  const rand = () => {
    t += 0x6d2b79f5;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}
